import { Plus, Trash2 } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import type { CatalogEntryView, CreatePartLineInput, EtaInput } from "../../../../shared/contracts.js";
import { LIMITS } from "../../../../shared/contracts.js";
import { normalizePartNumber } from "../../../../shared/formatting.js";
import { fetchCatalogLookup, isSuggestPrefix, useCatalogLookup, useCatalogSuggest } from "../../../api/catalogQueries.js";
import { Button } from "../../../ui/Button.js";
import { TextField } from "../../../ui/FormField.js";

/**
 * The parts grid both order forms share — the service order form and its
 * edit sheet, and the wholesale order form and its edit sheet (spec §5
 * "Part-line fields", §14; wholesale spec §4.3, §6.2, §6.5): the line's
 * editing type, its validation, the mapping to the shared
 * `CreatePartLineInput`, the master-ETA fill, and the repeatable lines
 * themselves.
 *
 * Each line is a fieldset with part number, description, quantity (1–999)
 * and exactly one ETA choice: a calendar date, or the explicit "No ETA" box
 * which disables the date. Adding a line moves focus into it; removing one
 * moves focus to the Add button so the keyboard never lands nowhere. The
 * form always keeps at least one line — an order has one or more.
 *
 * The grid is generic over the line type so an edit sheet can carry server
 * facts (line id, verified quantity, exception) beside the text fields:
 * `canRemove` withholds Remove for lines that are records, `lineDisabled`
 * locks a line that is on record but cannot be edited from here, and
 * `renderLineExtras` adds the sheet's exception controls.
 *
 * The part-number field talks to the parts catalog (spec §6.2): from the
 * third character a listbox of matches opens under it (arrow keys, Enter,
 * Escape, or a click), and leaving the field looks the exact number up.
 * Either fills the description only while it is blank or still the
 * catalog's earlier answer — never over words Parts typed — and a filled
 * description says "From the catalog" beneath it.
 *
 * Keyboard first (wholesale spec §4.3, built for 50–100 orders a day): Tab
 * moves through the grid; **Enter** on the last line adds a row and lands
 * on its part number (an untouched last line is already that row, so
 * Enter there adds nothing), on any earlier line it jumps to the next
 * line, and never submits the form from inside the grid; a **pasted
 * list** of `part number<TAB>quantity` lines fills the pasted-into line
 * and appends one row per further line, looking every number up in the
 * catalog. The row Enter leaves behind after the last part is dropped by
 * every form on submit ({@link withoutBlankTrailingLines}), never refused.
 *
 * "ETA for all parts" — the master ETA — fills every line whose ETA is
 * blank and follows those lines (`etaFromMaster`) when it changes; a line
 * with its own date or with No ETA chosen is never touched, and clearing
 * it changes no line ({@link withMasterEta}).
 */

// ---------------------------------------------------------------------------
// Editing types
// ---------------------------------------------------------------------------

export type OrderFormLineValues = {
  clientId: string;
  partNumber: string;
  description: string;
  quantityOrdered: string;
  etaDate: string;
  noEta: boolean;
  /** The description the catalog last filled in; blank once Parts typed over it. */
  catalogDescription?: string;
  /** True while `etaDate` is the master ETA's doing rather than a hand-set date. */
  etaFromMaster?: boolean;
};

export type OrderFormLineFieldKey = "partNumber" | "description" | "quantityOrdered" | "eta";

export type OrderFormLineErrors = Partial<Record<OrderFormLineFieldKey, string>>;

let nextClientId = 0;

/** A stable key for a line while it is being edited; never sent to the server. */
export function newClientId(): string {
  nextClientId += 1;
  const random =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `line-${nextClientId}-${random}`;
}

/**
 * A blank line: quantity 1 (the common order), no ETA choice made yet —
 * unless the order has a master ETA, which the new line starts from.
 */
export function newLineValues(masterEta = ""): OrderFormLineValues {
  const line: OrderFormLineValues = {
    clientId: newClientId(),
    partNumber: "",
    description: "",
    quantityOrdered: "1",
    etaDate: "",
    noEta: false,
  };
  return isCalendarDate(masterEta.trim()) ? { ...line, etaDate: masterEta.trim(), etaFromMaster: true } : line;
}

/**
 * True while nothing has been typed or chosen on the line. A date the
 * master ETA handed the line (`etaFromMaster`) is not Parts' doing, so a
 * fresh row under "ETA for all parts" is still blank.
 */
export function isBlankLine(line: OrderFormLineValues): boolean {
  return (
    line.partNumber === "" &&
    line.description === "" &&
    line.quantityOrdered === "1" &&
    (line.etaDate === "" || line.etaFromMaster === true) &&
    !line.noEta
  );
}

/**
 * The lines with the untouched rows at the end dropped — the row Enter
 * adds after the last part, or a spare Add — so a blank trailing line
 * never blocks a submit (wholesale spec §4.3 invites the habit). The
 * first line always stays: an order has one or more lines, and a form
 * with nothing typed is for validation to name. A blank line *between*
 * typed ones is not trailing and stays too.
 */
export function withoutBlankTrailingLines<L extends OrderFormLineValues>(lines: readonly L[]): L[] {
  let end = lines.length;
  while (end > 1 && isBlankLine(lines[end - 1]!)) end -= 1;
  return lines.slice(0, end);
}

/**
 * The lines after "ETA for all parts" was set to `masterEta` (spec §6.5):
 * a line whose ETA is blank, or whose date the master ETA gave it, takes
 * the new date; a line with its own date or with No ETA chosen keeps it.
 * A blank or invalid master ETA changes nothing — clearing it never
 * empties a line.
 */
export function withMasterEta<L extends OrderFormLineValues>(lines: readonly L[], masterEta: string): L[] {
  const date = masterEta.trim();
  if (!isCalendarDate(date)) return [...lines];
  return lines.map((line) => {
    if (line.noEta) return line;
    if (line.etaDate.trim() !== "" && !line.etaFromMaster) return line;
    return { ...line, etaDate: date, etaFromMaster: true };
  });
}

// ---------------------------------------------------------------------------
// Messages and validation
// ---------------------------------------------------------------------------

export const LINE_MESSAGES = {
  partNumber: "Enter the part number.",
  partNumberKey: "A part number needs at least one letter or digit.",
  partNumberLength: `Use ${LIMITS.partNumber.min}–${LIMITS.partNumber.max} characters.`,
  description: "Enter a description.",
  descriptionLength: `Use ${LIMITS.description.min}–${LIMITS.description.max} characters.`,
  quantityOrdered: `Enter a quantity from ${LIMITS.quantity.min} through ${LIMITS.quantity.max}.`,
  eta: "Enter an ETA date or choose No ETA.",
  masterEta: "Enter a valid date.",
} as const;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** True for a real calendar date in `YYYY-MM-DD` form. */
export function isCalendarDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/** A whole number parsed from what was typed, or `null`. */
export function parseWholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export function validateLine(line: OrderFormLineValues): OrderFormLineErrors {
  const errors: OrderFormLineErrors = {};
  const partNumber = line.partNumber.trim();
  if (partNumber.length === 0) errors.partNumber = LINE_MESSAGES.partNumber;
  else if (partNumber.length > LIMITS.partNumber.max) errors.partNumber = LINE_MESSAGES.partNumberLength;
  else if (normalizePartNumber(partNumber).length === 0) errors.partNumber = LINE_MESSAGES.partNumberKey;

  const description = line.description.trim();
  if (description.length === 0) errors.description = LINE_MESSAGES.description;
  else if (description.length > LIMITS.description.max) errors.description = LINE_MESSAGES.descriptionLength;

  const quantity = parseWholeNumber(line.quantityOrdered);
  if (quantity === null || quantity < LIMITS.quantity.min || quantity > LIMITS.quantity.max) {
    errors.quantityOrdered = LINE_MESSAGES.quantityOrdered;
  }

  if (!line.noEta && !isCalendarDate(line.etaDate.trim())) errors.eta = LINE_MESSAGES.eta;
  return errors;
}

/** The line field a server path segment names (`eta` covers `eta.kind` and `eta.date`). */
export function lineFieldKey(segment: string | undefined): OrderFormLineFieldKey | null {
  switch (segment) {
    case "partNumber":
    case "description":
    case "quantityOrdered":
    case "eta":
      return segment;
    default:
      return null;
  }
}

/**
 * The line marks left after Parts changed a line: a mark on a field whose
 * text changed is dropped at once, so a corrected field stops being
 * marked. Shared by every form that owns a grid.
 */
export function clearChangedLineMarks<L extends OrderFormLineValues>(
  marks: Readonly<Record<string, OrderFormLineErrors>>,
  previousLines: readonly L[],
  lines: readonly L[],
): Readonly<Record<string, OrderFormLineErrors>> {
  const next = { ...marks };
  let changed = false;
  for (const line of lines) {
    const existing = next[line.clientId];
    const previous = previousLines.find((candidate) => candidate.clientId === line.clientId);
    if (!existing || !previous) continue;
    const kept = { ...existing };
    if (previous.partNumber !== line.partNumber) delete kept.partNumber;
    if (previous.description !== line.description) delete kept.description;
    if (previous.quantityOrdered !== line.quantityOrdered) delete kept.quantityOrdered;
    if (previous.etaDate !== line.etaDate || previous.noEta !== line.noEta) delete kept.eta;
    if (Object.keys(kept).length === Object.keys(existing).length) continue;
    changed = true;
    if (Object.keys(kept).length === 0) delete next[line.clientId];
    else next[line.clientId] = kept;
  }
  return changed ? next : marks;
}

// ---------------------------------------------------------------------------
// Mapping to the shared contract
// ---------------------------------------------------------------------------

export function toEtaInput(line: Pick<OrderFormLineValues, "etaDate" | "noEta">): EtaInput {
  return line.noEta ? { kind: "no_eta" } : { kind: "date", date: line.etaDate.trim() };
}

export function toPartLineInput(line: OrderFormLineValues): CreatePartLineInput {
  return {
    partNumber: line.partNumber.trim(),
    description: line.description.trim(),
    quantityOrdered: parseWholeNumber(line.quantityOrdered) ?? Number.NaN,
    eta: toEtaInput(line),
  };
}

// ---------------------------------------------------------------------------
// "ETA for all parts"
// ---------------------------------------------------------------------------

export interface MasterEtaFieldProps {
  /** As typed (`YYYY-MM-DD` or blank). */
  value: string;
  error?: string | null;
  onChange: (masterEta: string) => void;
  disabled?: boolean;
  /** Prefix for the control id, so two forms on one page never collide. */
  idPrefix: string;
}

/**
 * "ETA for all parts" (spec §6.5), at the head of the Parts ordered
 * section: one date that fills every line left blank below it. The caller
 * applies {@link withMasterEta} to the lines on change.
 */
export function MasterEtaField({ value, error, onChange, disabled, idPrefix }: MasterEtaFieldProps) {
  return (
    <div className="order-form__grid order-form__grid--master-eta">
      <TextField
        id={`${idPrefix}-masterEta`}
        label="ETA for all parts"
        type="date"
        optional="untagged"
        hint="Fills every line that has no ETA yet. Lines with their own date or No ETA keep it."
        error={error ?? null}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pasted lists
// ---------------------------------------------------------------------------

export type PastedLine = Readonly<{ partNumber: string; quantity: string }>;

/**
 * A pasted list of parts — one per line, `part number<TAB>quantity` (a
 * comma or a run of spaces separates too), the quantity optional and 1
 * when missing, blank lines skipped — or `null` when the text is not a
 * list at all (one plain value, or nothing), in which case the paste is
 * left to the field it landed in.
 */
export function parsePastedLines(text: string): PastedLine[] | null {
  const rows = text
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter((row) => row.length > 0);
  if (rows.length === 0) return null;
  const parsed = rows.map((row) => {
    const [first = "", second = ""] = row.split(/\t|,|\s{2,}/).map((cell) => cell.trim());
    const quantity = /^\d+$/.test(second) ? second : "1";
    return { partNumber: first, quantity };
  });
  const isList = rows.length > 1 || /\t|,|\s{2,}/.test(rows[0] ?? "");
  return isList ? parsed.filter((row) => row.partNumber !== "") : null;
}

// ---------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------

export interface PartLinesGridProps<L extends OrderFormLineValues> {
  lines: readonly L[];
  /** Keyed by `clientId`. */
  errors: Readonly<Record<string, OrderFormLineErrors>>;
  onChange: (lines: L[]) => void;
  /** Builds a new line; defaults to a blank `OrderFormLineValues`. */
  createLine?: () => L;
  /** Whether Remove renders for this line; defaults to every line. */
  canRemove?: (line: L) => boolean;
  /** Extra content per line (the edit sheet's exception controls). */
  renderLineExtras?: (line: L, index: number) => ReactNode;
  /** The lowest quantity a line accepts (an edit cannot order fewer than verified). */
  minQuantity?: (line: L) => number;
  /**
   * Whether this line's fields refuse input while the rest of the grid
   * stays live (a line saved from the sheet that cannot be edited again
   * until it reopens). Defaults to no line.
   */
  lineDisabled?: (line: L) => boolean;
  disabled?: boolean;
  idPrefix: string;
}

export function PartLinesGrid<L extends OrderFormLineValues>({
  lines,
  errors,
  onChange,
  createLine,
  canRemove,
  renderLineExtras,
  minQuantity,
  lineDisabled,
  disabled = false,
  idPrefix,
}: PartLinesGridProps<L>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const [focusRequest, setFocusRequest] = useState<
    { kind: "line"; clientId: string } | { kind: "add" } | null
  >(null);
  // The latest lines, for handlers that fire after several state updates
  // (a pasted list's lookups answer one by one).
  const linesRef = useRef(lines);
  linesRef.current = lines;

  useEffect(() => {
    if (!focusRequest) return;
    if (focusRequest.kind === "add") {
      addRef.current?.focus();
    } else {
      const target = document.getElementById(controlId(idPrefix, focusRequest.clientId, "partNumber"));
      if (target && rootRef.current?.contains(target)) target.focus();
    }
    setFocusRequest(null);
  }, [focusRequest, idPrefix]);

  const build = () => (createLine ?? (newLineValues as () => L))();

  const updateLine = (clientId: string, patch: Partial<OrderFormLineValues>) => {
    onChange(linesRef.current.map((line) => (line.clientId === clientId ? { ...line, ...patch } : line)));
  };

  const addLine = () => {
    const line = build();
    onChange([...linesRef.current, line]);
    setFocusRequest({ kind: "line", clientId: line.clientId });
  };

  const removeLine = (clientId: string) => {
    onChange(linesRef.current.filter((line) => line.clientId !== clientId));
    setFocusRequest({ kind: "add" });
  };

  /**
   * Enter on the last line adds a row; on any other line it jumps to the
   * next line's part number. A last line still untouched *is* the spare
   * row, so Enter there adds nothing — blank rows never stack.
   */
  const onEnter = (index: number) => {
    const line = lines[index];
    if (index >= lines.length - 1) {
      if (line && isBlankLine(line)) {
        setFocusRequest({ kind: "line", clientId: line.clientId });
        return;
      }
      addLine();
      return;
    }
    const next = lines[index + 1];
    if (next) setFocusRequest({ kind: "line", clientId: next.clientId });
  };

  /**
   * A pasted list fills the pasted-into line and appends one row per
   * further line; every number is then looked up so known parts get their
   * description (blank or the catalog's earlier words only, as on blur).
   */
  const onPasteList = (clientId: string, pasted: readonly PastedLine[]) => {
    const [first, ...rest] = pasted;
    if (!first) return;
    const appended = rest.map((row) => ({ ...build(), partNumber: row.partNumber, quantityOrdered: row.quantity }));
    const next = linesRef.current.map((line) =>
      line.clientId === clientId ? { ...line, partNumber: first.partNumber, quantityOrdered: first.quantity } : line,
    );
    const all = [...next, ...appended];
    onChange(all);
    linesRef.current = all;
    const targets = [
      { clientId, partNumber: first.partNumber },
      ...appended.map((line) => ({ clientId: line.clientId, partNumber: line.partNumber })),
    ];
    for (const target of targets) {
      if (normalizePartNumber(target.partNumber).length === 0) continue;
      void fetchCatalogLookup(target.partNumber).then(
        (entry) => {
          if (!entry) return;
          const current = linesRef.current.find((line) => line.clientId === target.clientId);
          if (!current || current.partNumber !== target.partNumber || !fillable(current)) return;
          updateLine(target.clientId, { description: entry.description, catalogDescription: entry.description });
          linesRef.current = linesRef.current.map((line) =>
            line.clientId === target.clientId
              ? { ...line, description: entry.description, catalogDescription: entry.description }
              : line,
          );
        },
        () => undefined, // an unreachable catalog leaves the descriptions for Parts to type
      );
    }
  };

  const removableCount = lines.filter((line) => canRemove?.(line) ?? true).length;

  return (
    <div ref={rootRef} className="part-lines">
      <ol className="part-lines__list">
        {lines.map((line, index) => {
          const locked = disabled || (lineDisabled?.(line) ?? false);
          return (
            <li key={line.clientId} className="part-lines__item">
              <PartLineFields
                line={line}
                index={index}
                errors={errors[line.clientId] ?? {}}
                onChange={(patch) => updateLine(line.clientId, patch)}
                onEnter={() => onEnter(index)}
                onPasteList={(pasted) => onPasteList(line.clientId, pasted)}
                onRemove={(canRemove?.(line) ?? true) ? () => removeLine(line.clientId) : undefined}
                // The last removable line cannot go: an order has one or more lines.
                removeDisabled={locked || (lines.length === 1 && removableCount === 1)}
                minQuantity={minQuantity?.(line) ?? LIMITS.quantity.min}
                disabled={locked}
                idPrefix={idPrefix}
                extras={renderLineExtras?.(line, index)}
              />
            </li>
          );
        })}
      </ol>
      <div className="part-lines__footer">
        <Button ref={addRef} variant="secondary" icon={<Plus />} onClick={addLine} disabled={disabled}>
          Add part line
        </Button>
      </div>
    </div>
  );
}

export function controlId(idPrefix: string, clientId: string, field: string): string {
  return `${idPrefix}-line-${clientId}-${field}`;
}

/** Whether an auto-fill may write the description: blank, or still the catalog's earlier answer. */
function fillable(current: OrderFormLineValues): boolean {
  return (
    current.description.trim() === "" ||
    (current.catalogDescription !== undefined &&
      current.catalogDescription !== "" &&
      current.description === current.catalogDescription)
  );
}

interface PartLineFieldsProps {
  line: OrderFormLineValues;
  index: number;
  errors: OrderFormLineErrors;
  onChange: (patch: Partial<OrderFormLineValues>) => void;
  /** Enter in one of the line's fields, with no suggestion chosen. */
  onEnter: () => void;
  /** A list was pasted into the part-number field. */
  onPasteList: (pasted: readonly PastedLine[]) => void;
  /** Absent when the line is a record that can only be flagged, never removed. */
  onRemove: (() => void) | undefined;
  removeDisabled: boolean;
  minQuantity: number;
  disabled: boolean;
  idPrefix: string;
  extras: ReactNode;
}

function PartLineFields({
  line,
  index,
  errors,
  onChange,
  onEnter,
  onPasteList,
  onRemove,
  removeDisabled,
  minQuantity,
  disabled,
  idPrefix,
  extras,
}: PartLineFieldsProps) {
  const id = (field: string) => controlId(idPrefix, line.clientId, field);
  const number = index + 1;

  // The catalog: suggestions while typing the number, an exact lookup on
  // leaving it. The latest line is read through a ref when a lookup answers,
  // because Parts may have typed a description in the meantime.
  const lineRef = useRef(line);
  lineRef.current = line;
  const lookup = useCatalogLookup();
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const suggestions = useCatalogSuggest(line.partNumber, { enabled: suggestOpen && !disabled });
  // Nothing is listed under text too short to have asked for it, whatever
  // the query still holds from an earlier, longer prefix.
  const items: readonly CatalogEntryView[] =
    suggestOpen && isSuggestPrefix(line.partNumber) ? (suggestions.data ?? []) : [];
  const listOpen = items.length > 0;
  const listboxId = id("suggestions");
  const fromCatalog =
    line.catalogDescription !== undefined &&
    line.catalogDescription !== "" &&
    line.description === line.catalogDescription;

  const choose = (entry: CatalogEntryView) => {
    const current = lineRef.current;
    onChange({
      partNumber: entry.partNumber,
      ...(fillable(current) ? { description: entry.description, catalogDescription: entry.description } : {}),
    });
    setSuggestOpen(false);
    setActiveIndex(-1);
  };

  const lookUpOnLeave = () => {
    setSuggestOpen(false);
    setActiveIndex(-1);
    const typed = line.partNumber;
    if (normalizePartNumber(typed).length === 0) return;
    void lookup(typed).then(
      (entry) => {
        const current = lineRef.current;
        if (!entry || current.partNumber !== typed || !fillable(current)) return;
        if (current.description === entry.description) return;
        onChange({ description: entry.description, catalogDescription: entry.description });
      },
      () => undefined, // an unreachable catalog leaves the description for Parts to type
    );
  };

  /** Enter inside the grid never submits the form: it adds or moves to a line. */
  const onFieldKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    onEnter();
  };

  const onPartNumberKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" && suggestOpen) {
      event.preventDefault();
      setSuggestOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const entry = listOpen && activeIndex >= 0 ? items[activeIndex] : undefined;
      if (entry) choose(entry);
      else onEnter();
      return;
    }
    if (!listOpen) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? items.length - 1 : current - 1));
    }
  };

  const onPartNumberPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = parsePastedLines(event.clipboardData.getData("text"));
    if (!pasted || pasted.length === 0) return;
    event.preventDefault();
    setSuggestOpen(false);
    setActiveIndex(-1);
    onPasteList(pasted);
  };

  return (
    <fieldset className="part-line" data-line-id={line.clientId} disabled={disabled}>
      <legend className="part-line__legend">Part line {number}</legend>
      <div className="part-line__grid">
        <div className="part-line__part-number">
          <TextField
            id={id("partNumber")}
            label="Part number"
            error={errors.partNumber ?? null}
            value={line.partNumber}
            onChange={(event) => {
              onChange({ partNumber: event.target.value });
              setSuggestOpen(true);
              setActiveIndex(-1);
            }}
            onBlur={lookUpOnLeave}
            onKeyDown={onPartNumberKeyDown}
            onPaste={onPartNumberPaste}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={listOpen}
            aria-controls={listOpen ? listboxId : undefined}
            aria-activedescendant={listOpen && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
            autoComplete="off"
            spellCheck={false}
            maxLength={LIMITS.partNumber.max + 10}
          />
          {listOpen ? (
            <ul
              id={listboxId}
              role="listbox"
              aria-label={`Suggestions for part line ${number}`}
              className="part-line__suggestions"
            >
              {items.map((entry, optionIndex) => (
                <li
                  key={entry.normalizedPartNumber}
                  id={`${listboxId}-${optionIndex}`}
                  role="option"
                  aria-selected={optionIndex === activeIndex}
                  className="part-line__suggestion"
                  data-active={optionIndex === activeIndex ? "true" : undefined}
                  // Mouse-down would blur the input (closing the list) before the click lands.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(entry)}
                >
                  <span className="part-line__suggestion-number">{entry.partNumber}</span>
                  <span className="part-line__suggestion-description">{entry.description}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <TextField
          id={id("description")}
          label="Description"
          hint={fromCatalog ? "From the catalog." : undefined}
          error={errors.description ?? null}
          value={line.description}
          onChange={(event) => {
            const description = event.target.value;
            onChange({
              description,
              ...(description !== line.catalogDescription ? { catalogDescription: "" } : {}),
            });
          }}
          onKeyDown={onFieldKeyDown}
          autoComplete="off"
          maxLength={LIMITS.description.max + 10}
          fieldClassName="part-line__description"
        />
        <TextField
          id={id("quantityOrdered")}
          label="Quantity"
          type="number"
          inputMode="numeric"
          min={minQuantity}
          max={LIMITS.quantity.max}
          step={1}
          error={errors.quantityOrdered ?? null}
          value={line.quantityOrdered}
          onChange={(event) => onChange({ quantityOrdered: event.target.value })}
          onKeyDown={onFieldKeyDown}
          fieldClassName="part-line__quantity"
        />
        <div className="part-line__eta">
          <TextField
            id={id("eta")}
            label="ETA"
            type="date"
            error={errors.eta ?? null}
            value={line.noEta ? "" : line.etaDate}
            onChange={(event) => onChange({ etaDate: event.target.value, etaFromMaster: false })}
            onKeyDown={onFieldKeyDown}
            disabled={disabled || line.noEta}
            fieldClassName="part-line__eta-date"
          />
          {/* The label wraps the box so the whole caption is the hit area
              (desktop controls are at least 28×28 CSS px). */}
          <label className="part-line__no-eta" htmlFor={id("noEta")}>
            <input
              id={id("noEta")}
              type="checkbox"
              className="checkbox-input"
              checked={line.noEta}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? { noEta: true, etaDate: "", etaFromMaster: false }
                    : { noEta: false },
                )
              }
            />
            <span className="part-line__no-eta-text">No ETA</span>
          </label>
        </div>
      </div>
      {extras ? <div className="part-line__extras">{extras}</div> : null}
      {onRemove ? (
        <div className="part-line__remove">
          <Button variant="quiet" icon={<Trash2 />} iconOnly onClick={onRemove} disabled={removeDisabled}>
            Remove part line {number}
          </Button>
        </div>
      ) : null}
    </fieldset>
  );
}
